// src/resources/units/components/BulkRevokeKeysModal.tsx
import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useUnitKeyManagement } from '../../../services/api/useUnitKeyManagement';

interface BulkRevokeKeysModalProps {
  open: boolean;
  onClose: () => void;
  units: Array<{ id: number; serial?: string; hostname?: string }>;
  onSuccess: (successCount: number, failureCount: number) => void;
  onError: (error: any) => void;
}

const BulkRevokeKeysModal: FC<BulkRevokeKeysModalProps> = ({ open, onClose, units, onSuccess, onError }) => {
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<{
    succeeded: Array<{ identifier: string; message: string }>;
    failed: Array<{ identifier: string; error: string }>;
  } | null>(null);

  const { revokeKeys } = useUnitKeyManagement();

  const handleReset = () => {
    setReason('');
    setResults(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setResults(null);

    try {
      // Get identifiers - prefer serial, fallback to hostname
      const identifiers = units.map((unit) => unit.serial || unit.hostname || `unit-${unit.id}`).filter(Boolean);

      const response = await revokeKeys(identifiers, reason || undefined);

      setResults({
        succeeded: response.data.succeeded,
        failed: response.data.failed,
      });

      // If all succeeded, auto-close after brief delay
      if (response.data.failure_count === 0) {
        setTimeout(() => {
          onSuccess(response.data.success_count, response.data.failure_count);
          handleClose();
        }, 1500);
      } else {
        // If partial failure, keep modal open to show results
        onSuccess(response.data.success_count, response.data.failure_count);
      }
    } catch (error: any) {
      onError(error);
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Revoke SSH Keys for Multiple Units</DialogTitle>
      <DialogContent>
        {!results && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to revoke SSH keys for {units.length} unit(s). This will force device re-registration.
            </Alert>

            <Typography variant="body2" sx={{ mb: 2 }}>
              Units to revoke:
            </Typography>

            <List dense sx={{ mb: 2, maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
              {units.map((unit) => (
                <ListItem key={unit.id}>
                  <ListItemText
                    primary={unit.serial || unit.hostname || `Unit ${unit.id}`}
                    secondary={unit.serial && unit.hostname ? unit.hostname : undefined}
                  />
                </ListItem>
              ))}
            </List>

            <TextField
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={isLoading}
              placeholder="Enter reason for bulk revocation..."
              inputProps={{ maxLength: 1000 }}
              helperText={`${reason.length}/1000 characters`}
            />
          </>
        )}

        {results && (
          <Box>
            {results.succeeded.length > 0 && (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Successfully revoked {results.succeeded.length} SSH key(s)
                </Alert>
                <List dense>
                  {results.succeeded.map((item) => (
                    <ListItem key={item.identifier}>
                      <ListItemText primary={item.identifier} secondary={item.message} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {results.failed.length > 0 && (
              <>
                <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                  Failed to revoke {results.failed.length} key(s)
                </Alert>
                <List dense>
                  {results.failed.map((item) => (
                    <ListItem key={item.identifier}>
                      <ListItemText primary={item.identifier} secondary={item.error} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {results ? 'Close' : 'Cancel'}
        </Button>
        {!results && (
          <Button onClick={handleConfirm} variant="contained" color="error" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : `Revoke ${units.length} Key(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkRevokeKeysModal;
